// "הבית שלי" (Gate 2B): what the signed-in user should do next in the current project, derived
// only from data the server already returned to them (the calendar, the handovers, the team). The
// roles below choose which parts of Home are shown; they grant nothing, and every action is still
// authorized by the server.
import {
  ACTIVE_APPOINTMENT_STATUSES,
  type Appointment,
  type Assignment,
  type HandoverApartmentListResponse,
  type HandoverSummary,
  type MeResponse,
  type ProjectTeamResponse,
  type ScheduleResponse,
  calendarDate,
} from '@sivan/shared';

/** The parts of Home the user's permissions in the project call for. */
export interface HomeRoles {
  /** Performs handovers (edits drafts). */
  readonly field: boolean;
  /** Decides who performs them (approves, assigns or manages the calendar). */
  readonly coordinator: boolean;
  /** Manages the team or the projects. */
  readonly admin: boolean;
  /** Oversees the project: its status, handovers and defects (the "מנהל פרויקט" template). */
  readonly manager: boolean;
}

/** The user's business keys in a project (organization-wide and project-specific). */
export function keysIn(me: MeResponse, projectId: string): ReadonlySet<string> {
  return new Set([
    ...me.access.organization,
    ...me.access.allProjects,
    ...(me.access.projects.find((p) => p.projectId === projectId)?.permissions ?? []),
  ]);
}

export function rolesOf(keys: ReadonlySet<string>): HomeRoles {
  return {
    field: keys.has('handover.handover.edit'),
    coordinator:
      keys.has('schedule.assignment.approve') ||
      keys.has('schedule.assignment.assign') ||
      keys.has('schedule.calendar.manage'),
    admin:
      keys.has('organization.access.manage') ||
      keys.has('organization.project.create') ||
      keys.has('projects.project.edit'),
    manager: keys.has('audit.project.view') && keys.has('schedule.calendar.manage'),
  };
}

const active = (a: Appointment) => ACTIVE_APPOINTMENT_STATUSES.includes(a.status);
const byTime = (a: Appointment, b: Appointment) => a.startsAt.localeCompare(b.startsAt);

/** The employee assigned as the appointment's responsible person, if any. */
export const primaryOf = (a: Appointment): Assignment | undefined =>
  a.assignments.find((s) => s.role === 'primary' && s.status === 'assigned');

/** True when the caller (as an employee) is assigned to the appointment. */
export const isMine = (a: Appointment, employeeId: string | null): boolean =>
  employeeId !== null &&
  a.assignments.some((s) => s.employee.id === employeeId && s.status === 'assigned');

/** A request to be assigned, with its appointment (for the coordinator's decision). */
export interface PendingRequest {
  readonly assignment: Assignment;
  readonly appointment: Appointment;
}

/** One open draft, with its apartment. */
export interface OpenDraft {
  readonly apartment: HandoverApartmentListResponse['items'][number]['apartment'];
  readonly handover: HandoverSummary;
}

export interface HomeData {
  /** Today's appointments (not cancelled), by time. */
  readonly today: readonly Appointment[];
  /** Active appointments after today (within the loaded week), by time. */
  readonly upcoming: readonly Appointment[];
  /** The caller's own active appointments from now on, by time. */
  readonly mine: readonly Appointment[];
  /** Active appointments without a responsible employee. */
  readonly unassigned: readonly Appointment[];
  /** Requests to be assigned that wait for a decision. */
  readonly requests: readonly PendingRequest[];
  /** Open drafts not complete yet, most recently changed first. */
  readonly incomplete: readonly OpenDraft[];
  /** Open drafts with open findings or findings awaiting reinspection. */
  readonly attention: readonly OpenDraft[];
  /** Team members who cannot work in the system yet (no active account) or are inactive. */
  readonly teamIssues: readonly ProjectTeamResponse['members'][number][];
}

/**
 * Home's lists from what the server returned (any of the sources may be missing: the user may not
 * see it, or it failed to load). `now` decides today and the past, in Israel time.
 */
export function homeData(
  schedule: ScheduleResponse | null,
  handovers: HandoverApartmentListResponse | null,
  team: ProjectTeamResponse | null,
  now: Date,
): HomeData {
  const day = calendarDate(now);
  const appointments = [...(schedule?.appointments ?? [])].sort(byTime);
  const dayOf = (a: Appointment) => calendarDate(new Date(a.startsAt));
  const ends = (a: Appointment) => Date.parse(a.startsAt) + a.durationMinutes * 60_000;
  const employeeId = schedule?.me?.employeeId ?? null;
  const drafts: OpenDraft[] = (handovers?.items ?? [])
    .flatMap((item) =>
      // Every handover is a draft until signing exists (a later milestone).
      item.handovers.map((handover) => ({ apartment: item.apartment, handover })),
    )
    .sort((a, b) => b.handover.updatedAt.localeCompare(a.handover.updatedAt));
  return {
    today: appointments.filter((a) => dayOf(a) === day && a.status !== 'cancelled'),
    upcoming: appointments.filter((a) => dayOf(a) > day && active(a)),
    mine: appointments.filter(
      (a) => active(a) && isMine(a, employeeId) && ends(a) >= now.getTime(),
    ),
    unassigned: appointments.filter(
      (a) => active(a) && ends(a) >= now.getTime() && primaryOf(a) === undefined,
    ),
    requests: appointments
      .filter((a) => active(a))
      .flatMap((appointment) =>
        appointment.assignments
          .filter((assignment) => assignment.status === 'requested')
          .map((assignment) => ({ assignment, appointment })),
      ),
    incomplete: drafts.filter(
      ({ handover: { progress } }) => progress === null || progress.checked < progress.total,
    ),
    attention: drafts.filter(
      ({ handover: { progress } }) =>
        progress !== null && (progress.openFindings > 0 || progress.awaitingReinspection > 0),
    ),
    teamIssues: (team?.members ?? []).filter(
      (m) => m.employee.status !== 'active' || m.employee.account.state !== 'active',
    ),
  };
}

/** The open draft of an appointment's apartment and type, if the handovers list has it. */
export function draftOf(
  appointment: Appointment,
  handovers: HandoverApartmentListResponse | null,
): HandoverSummary | undefined {
  return handovers?.items
    .find((i) => i.apartment.id === appointment.apartment.id)
    ?.handovers.find((h) => h.type === appointment.type);
}

/** "בוקר טוב" / "צהריים טובים" / "ערב טוב" by the hour in Israel. */
export function greeting(now: Date): string {
  const hour = Number(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Jerusalem',
      hour: '2-digit',
      hourCycle: 'h23',
    }).format(now),
  );
  return hour < 12 ? 'בוקר טוב' : hour < 17 ? 'צהריים טובים' : 'ערב טוב';
}

/** The project's handovers at a glance: what is done, what is in work, what needs action. */
export interface ProjectStatus {
  readonly apartments: number;
  readonly completed: number;
  readonly refused: number;
  readonly inWork: number;
  readonly notStarted: number;
  readonly openDefects: number;
  /** Final handovers with initial defects not reinspected yet. */
  readonly awaitingReinspection: number;
}

export function projectStatus(list: HandoverApartmentListResponse): ProjectStatus {
  let completed = 0;
  let refused = 0;
  let inWork = 0;
  let notStarted = 0;
  let openDefects = 0;
  let awaitingReinspection = 0;
  for (const item of list.items) {
    if (item.handovers.length === 0) notStarted += 1;
    for (const h of item.handovers) {
      if (h.status === 'completed') completed += 1;
      else if (h.status === 'refused') refused += 1;
      else if (h.status === 'draft') inWork += 1;
      openDefects += h.progress?.openFindings ?? 0;
      awaitingReinspection += h.progress?.awaitingReinspection ?? 0;
    }
  }
  return {
    apartments: list.items.length,
    completed,
    refused,
    inWork,
    notStarted,
    openDefects,
    awaitingReinspection,
  };
}
